// 데모 계정(username: demo)의 예약. BE `/api/schedules/*` 를 타지 않고
// 브라우저 localStorage 에만 둔다. 화면이 읽는 필드는 실계정 응답과 같게 맞춘다
// (id/enabled/hour/minute/daysOfWeek/sectionId/sectionName/nextRunAt).
//
// 실계정은 BE 스케줄러가 예약 시각에 비행을 건다. 데모는 그 서버가 없으므로
// DemoScenarioContext 의 틱이 이 목록을 읽어 직접 건다 — 그래서 "브라우저가 켜져 있는 동안만" 돈다.
//
// 임시 저장이라 브라우저를 바꾸거나 저장소를 비우면 사라진다 — 시연용으로만 쓴다.

const STORAGE_KEY = "demo-schedules";

export const DAY_KEYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const DAY_INDEX = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };

const pad = (n) => String(n).padStart(2, "0");

// 실계정 에러를 읽는 쪽(SchedulerPanel 의 explain)이 error.response.data.error.code 를
// 보므로, 같은 모양으로 던져서 분기 코드를 그대로 쓴다.
export class DemoScheduleError extends Error {
  constructor(code) {
    super(code);
    this.name = "DemoScheduleError";
    this.response = { data: { error: { code } } };
  }
}

const read = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // 사생활 보호 모드 등에서 읽기가 막혀도 시연은 계속돼야 한다.
    return [];
  }
};

const write = (list) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // 저장 실패는 화면 동작을 막지 않는다. 새로고침하면 사라질 뿐이다.
  }
  return list;
};

// BE 는 시간대 없는 Asia/Seoul 벽시계 문자열을 준다. 화면의 formatNextRun 이
// `new Date(value)` 로 로컬 파싱하므로 같은 모양으로 만들어야 시각이 안 밀린다.
const toWallClock = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
  `T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;

export const computeNextRunAt = (schedule, now = new Date()) => {
  const days = (schedule?.daysOfWeek ?? [])
    .map((day) => DAY_INDEX[day])
    .filter((index) => index != null);
  if (!days.length) return null;

  // 오늘 포함 8일을 훑으면 어떤 요일 조합이든 반드시 한 번은 걸린다.
  for (let ahead = 0; ahead < 8; ahead += 1) {
    const candidate = new Date(now);
    candidate.setDate(candidate.getDate() + ahead);
    candidate.setHours(Number(schedule.hour) || 0, Number(schedule.minute) || 0, 0, 0);
    if (!days.includes(candidate.getDay())) continue;
    if (candidate > now) return toWallClock(candidate);
  }
  return null;
};

// 지금이 이 예약의 발화 분(分)인가. computeNextRunAt 은 "미래"만 돌려주므로
// 발화 판정에는 못 쓴다 — 시각이 되는 순간 값이 다음 주로 넘어가기 때문.
// 그래서 요일·시·분을 직접 맞춰 본다. 판정 단위가 분이라 그 분 안에서는 몇 번을 물어도 참이고,
// 중복 발화는 부르는 쪽이 firedKey 로 막는다.
export const isDemoScheduleDue = (schedule, now = new Date()) => {
  if (!schedule?.enabled) return false;
  const days = schedule.daysOfWeek ?? [];
  if (!days.includes(DAY_KEYS[now.getDay()])) return false;
  return Number(schedule.hour) === now.getHours() && Number(schedule.minute) === now.getMinutes();
};

// 예약 1건이 "이번 분"에 이미 돌았는지 가리는 열쇠. 분이 바뀌면 값이 달라진다.
export const demoScheduleFireKey = (schedule, now = new Date()) =>
  `${schedule.id}@${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
  `T${pad(now.getHours())}:${pad(now.getMinutes())}`;

const sortByTime = (list) =>
  [...list].sort((a, b) => a.hour - b.hour || a.minute - b.minute);

// 다음 실행 시각은 저장하지 않고 읽을 때마다 다시 계산한다.
// 저장해 두면 하루만 지나도 "다음 실행"이 과거로 남는다.
export const listDemoSchedules = (now = new Date()) =>
  sortByTime(read()).map((schedule) => ({
    ...schedule,
    nextRunAt: computeNextRunAt(schedule, now),
  }));

const sameSlot = (a, b) =>
  a.hour === b.hour &&
  a.minute === b.minute &&
  [...(a.daysOfWeek ?? [])].sort().join() === [...(b.daysOfWeek ?? [])].sort().join();

// NaN 은 `?? null` 을 통과하지만 JSON.stringify 에서 null 이 된다.
// 그대로 두면 "섹션을 골랐는데 전체 칸이 나가는" 조용한 실패가 된다.
const normalizeSectionId = (value) => {
  if (value == null) return null;
  if (typeof value === "number" && !Number.isFinite(value)) return null;
  return value;
};

const fromPayload = (payload) => ({
  hour: Number(payload?.timeOfDay?.hour) || 0,
  minute: Number(payload?.timeOfDay?.minute) || 0,
  daysOfWeek: Array.isArray(payload?.daysOfWeek) ? [...payload.daysOfWeek] : [],
  sectionId: normalizeSectionId(payload?.sectionId),
  sectionName: payload?.sectionName ?? null,
});

export const saveDemoSchedule = (id, payload) => {
  const list = read();
  const next = fromPayload(payload);
  // 실계정의 SCHEDULE-004 와 같은 규칙. 같은 요일·시각이 둘이면 어느 쪽이 도는지 알 수 없다.
  if (list.some((item) => item.id !== id && sameSlot(item, next))) {
    throw new DemoScheduleError("SCHEDULE-004");
  }

  if (id) {
    return write(
      list.map((item) => (item.id === id ? { ...item, ...next } : item))
    );
  }
  return write([
    ...list,
    { id: `demo-schedule-${Date.now()}`, enabled: true, ...next },
  ]);
};

export const setDemoScheduleEnabled = (id, enabled) =>
  write(read().map((item) => (item.id === id ? { ...item, enabled } : item)));

export const removeDemoSchedule = (id) =>
  write(read().filter((item) => item.id !== id));
